import { useSelector } from "react-redux";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { RootState } from "../../redux/store";
import { useGetStudentClassesQuery } from "../../hooks/useGetStudentClassesQuery";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useGetAssignedTeachersClassesSubjectsQuery } from "../../hooks/useGetAssignedTeachersClassesSubjectsQuery";
import { useGetCoursesSubjectsQuery } from "../../hooks/useGetCoursesSubjectsQuery";
import { Button, Callout, Flex, Spinner } from "@radix-ui/themes";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useGetTeacherEvaluationCriteriasQuery } from "../../hooks/useGetTeacherEvaluationCriteriasQuery";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useState } from "react";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import { TeacherEvaluationCriteriaType, TeacherEvaluationType } from "../../types/TeacherEvaluation";
import { ChevronLeftIcon, EnterIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useMutation, useQueryClient } from "react-query";
import { Key_teacherEvaluationCriterias } from "../../utils/constants";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { createTeacherEvaluation, createTeachersEvaluationsCriterias } from "../../services/school/teacher/teacherEvaluationCriteria";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { routes } from "../../routes";

export const StudentTeacherEvaluation = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutationCreateTeacherEvaluation = useMutation({ mutationFn: createTeacherEvaluation, mutationKey: "createTeacherEvaluation" })
    const mutationCreateTeacherEvaluationsCriterias = useMutation({ mutationFn: createTeachersEvaluationsCriterias, mutationKey: "createTeachersEvaluationsCriterias" })
    
    const { UserId } = useSelector((state: RootState) => state.login.userData)
    const { data: studentData } = useGetTeacheIdByUserId(UserId, { enabled: !!UserId });
   
    const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();
    const { control, setValue, getValues, handleSubmit, formState: { errors } } = useForm<{ idClass: string, description: string, idTeacher: string, idSubject: string,  teacherEvaluations: { score: number, idTeacherEvaluationCriteria: string, description: string, idStudent: string, idTeacher: string, idSubject: string, idClass: string, idAcademicYear?: string }[] }>({
        defaultValues: { idClass: '', idSubject: "", idTeacher: "", teacherEvaluations: [], description: '' },
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const { data: classSubjectTeachers, isLoading: isLoadingClassSubjectTeachers, isError: isErrorClassSubjectTeachers, refetch: refreshClassSubjectTeacher } = useGetAssignedTeachersClassesSubjectsQuery(
        { PageNumber: 1, PageSize: 400, IdClass: getValues('idClass') },
        {
            enabled: !!getValues('idClass')
        }
    )
    const { data: studentClasses, isLoading: isLoadingStudentClasses, isError: isErrorStudentClasses } = useGetStudentClassesQuery({ PageNumber: 1, PageSize: 400, IdStudent: studentData?.idStudent }, { enabled: !!studentData?.idStudent })
    const { data: criterias, isLoading: isLoadingCriterias, isError: isErrorCriterias, refetch: refreshCriterias } = useGetTeacherEvaluationCriteriasQuery({ PageNumber: 1, PageSize: 400, IsForStudent: true, IdAcademicYear: currentAcademicYear?.id },
        {
            enabled: !!currentAcademicYear?.id
        }
    );

    const handleClassChange = (idClass: string) => {
        setValue('idClass', idClass);
        refreshClassSubjectTeacher();
    }
    const { fields } = useFieldArray({
        control,
        name: 'teacherEvaluations',
    });
    const handleTeacherChange = async (idTeacher: string) => {
        setValue('idTeacher', idTeacher);
        queryClient.invalidateQueries(Key_teacherEvaluationCriterias);
        await refreshCriterias();
        if (criterias && criterias.items) {
            setValue('teacherEvaluations',
                criterias.items.map(criteria => {
                    return {
                        score: 1,
                        idTeacherEvaluationCriteria: criteria?.id!,
                        description: criteria.description,
                        idClass: getValues('idClass'),
                        idStudent: studentData?.idStudent!,
                        idTeacher: getValues('idTeacher')?.split('/')?.[0]??"",
                        idSubject: classSubjectTeachers?.items.find(cst => cst.idClass === getValues('idClass') && cst.idTeacher === getValues('idTeacher'))?.idSubject!,
                        idAcademicYear: currentAcademicYear?.id
                    }
                })
            )
        }
    }
    const onSubmit = handleSubmit(async (data) => {
        const totalScore = data.teacherEvaluations.reduce((sum, evaluation) => Number(sum) + Number(evaluation.score), 0);
        const mediaScore = Math.round(totalScore / data.teacherEvaluations.length);
        const teacherEvaluation:TeacherEvaluationType = {
            idTeacher: data.idTeacher.split("/")?.[0]??"",
            idStudent: studentData?.idStudent!,
            classification: mediaScore,
            idAcademicYear: currentAcademicYear?.id!,
            idSubject: data.teacherEvaluations.find(te=> te.idSubject)?.idSubject!,
            description: data.description,
            idClass: data.idClass
        }

        try {
            const result = await mutationCreateTeacherEvaluation.mutateAsync(teacherEvaluation)
            if(!result) {toast.error(`Não foi possível guardar`); return;}
            var scores = data.teacherEvaluations.map(te=> {
                    return {
                        idTeacherEvaluationCriteria: te.idTeacherEvaluationCriteria, 
                        idTeacherEvaluation: result, 
                        score: te.score,
                        status: true,
                    }
            });
             await Promise.all(scores!.map((cv) => {
                return mutationCreateTeacherEvaluationsCriterias.mutateAsync(cv)
              }))
            toast.success(`Avaliação guardada`);
        } catch (err: any) {
            toast.error(`${err?.message}`);
        }

    });
    const handleBack = () => {
        navigate(routes.dashboard);
      }
      if (!studentData || !studentData.idStudent) {
        return (
          <Flex direction="column">
            <Callout.Root color="red" role="alert">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Callout.Text>
                Entre como estudante &nbsp;
                <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
              </Callout.Text>
            </Callout.Root>
    
          </Flex>
        );
      }
    return (
        <form onSubmit={onSubmit} noValidate>
            <Flex direction='column' gap='4'>
                <Flex gap='4'>
                    <SelectComponent
                        label="Turma"
                        options={studentClasses?.items.map((sc) => ({
                            value: sc.idClass,
                            label: sc.class ?? ""
                        }))}
                        errors={errors}
                        control={control}
                        itemsError={isErrorStudentClasses}
                        itemsLoading={isLoadingStudentClasses}
                        name={"idClass"}
                        onSelect={handleClassChange}
                        style={{ width: '100%' }}
                    />
                    <SelectComponent
                        label="Docente"
                        options={classSubjectTeachers?.items.map((sc, index) => ({
                            value:`${sc.idTeacher}/${index}`,
                            label: `${sc.teacher} | ${sc.subject}`
                        }))}
                        errors={errors}
                        control={control}
                        itemsError={isErrorClassSubjectTeachers}
                        itemsLoading={isLoadingClassSubjectTeachers}
                        name={"idTeacher"}
                        onSelect={handleTeacherChange}
                        style={{ width: '100%' }}
                    />
                </Flex>
                {fields?.map((criteria, index) => (
                    <Flex key={`${criteria.idTeacherEvaluationCriteria}-${index}`} direction="column" gap="2">
                        <Flex>
                            <RadioGroupComponent
                                name={`teacherEvaluations.${index}.score`}
                                label={criteria.description}
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={[
                                    { value: "1", label: "Pessimo" },
                                    { value: "2", label: "Mau" },
                                    { value: "3", label: "Normal" },
                                    { value: "4", label: "Bom" },
                                    { value: "5", label: "Excelente" }
                                ]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                                }}
                                itemsLoading={isLoadingCriterias}
                                itemsError={isErrorCriterias}
                            />
                        </Flex>
                    </Flex>
                ))}
                <TextFieldComponent name="description" label="Observação" control={control} errors={errors} fieldProps={{ placeholder: 'Observação' }} />
                <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
              <Button type="button" variant="soft" color="gray" disabled={mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading} onClick={() => {
                handleBack();
              }}
              >
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type='submit'
                disabled={mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading}

              >
                {mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                Registar
              </Button>
            </Flex>
            </Flex>
        </form>

    )
}
